import React, { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import devices from "styles/devices"
import { Button, ButtonType } from "components/atoms/Button"
import { Card } from "components/atoms/Card"
import Typography from "components/_blueprint/Typography/Typography"
import House from "url:assets/images/offer-accepted-status/house.png"
import MIP from "url:assets/images/offer-accepted-status/MIP.png"
import DogImage from "url:assets/images/offer-accepted-status/dog.svg"
import MojoMiniLogo from "url:assets/images/mojo-mini-logo-alt.svg"
import { PROPERTY_REPORT_URL } from "../../../Config"
import { EventAction, EventCategory } from "models/GoogleAnalytics"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { MortgageExpertCardComponent } from "./components/MortgageExpertCard"
import { ConveyancingCardComponent } from "./components/ConveyancingCard"
import { StatusContainer } from "./shared/StyledComponents"
import { StatusData } from "./utils/statusApiUtils"

interface OfferAcceptedViewProps {
  fetchedStatus?: StatusData | null
  friendlyId: string
}

const SmallFontButton = styled.div`
  font-size: 14px;
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 5px;
`

const BannerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OfferAcceptedBanner = styled.div`
  background-color: ${({ theme }) => theme.colors.status.warning};
  border-radius: 8px;
  padding: 8px 12px;
  width: fit-content;

  p {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    white-space: nowrap;
  }
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const OfferAcceptedHeading = styled.h4`
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const OfferAcceptedBody = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const TwoColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media screen and ${devices.bp_lg} {
    grid-template-columns: 574px 372px;
    align-items: stretch;
  }

  /* Ensure cards stretch to full height */
  > * {
    @media screen and ${devices.bp_lg} {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
`

const PropertyImageContainer = styled.div`
  position: relative;
  background-image: url(${House});
  background-size: cover;
  background-position: left;
  background-repeat: no-repeat;
  height: 277px;
  border-radius: 16px;
  margin: 0 0 32px 0;
`

const PropertyBadge = styled.div<{
  position: { top?: number; bottom?: number; left?: number; right?: number }
}>`
  position: absolute;
  ${(props) => props.position.top !== undefined && `top: ${props.position.top}px;`}
  ${(props) => props.position.bottom !== undefined && `bottom: ${props.position.bottom}px;`}
  ${(props) => props.position.left !== undefined && `left: ${props.position.left}px;`}
  ${(props) => props.position.right !== undefined && `right: ${props.position.right}px;`}
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: 182px;
  padding: 7px 16px;
  display: flex;
  align-items: center;
  gap: 11px;
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);

  span {
    font-weight: 500;
    font-size: 11px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

const GreenBadge = styled.div`
  position: absolute;
  top: 15px;
  left: 12px;
  background-color: #cefedb;
  border-radius: 262px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);

  .dot {
    width: 16px;
    height: 16px;
    background-color: #00c237;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  span {
    font-weight: 600;
    font-size: 12px;
    line-height: 12px;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

const EPCRating = styled.div`
  background-color: #8dce46;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const SignalBars = styled.div`
  display: flex;
  gap: 1px;
  align-items: end;
`

const SignalBar = styled.div<{ height: number; active: boolean }>`
  width: 4px;
  height: ${(props) => props.height}px;
  background-color: ${(props) => (props.active ? "#5bfb88" : "#d1ddfc")};
  border-radius: 14px;
`

const MIPImageContainer = styled.div`
  background-image: url(${MIP});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  height: 292px;
  border-radius: 16px;
  margin-bottom: 32px;
`

const PropertyCardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 100%;
`

const MIPCardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 100%;
  gap: 32px;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.status.error};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 8px;
  text-align: left;
`

const HadAnOfferAcceptedContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  padding: 5px;
`

const HadAnOfferAcceptedContent = styled.div`
  flex: 0;
  min-width: 535px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HadAnOfferAcceptedIllustration = styled.div`
  flex: 1;
  background-image: url(${DogImage});
  background-repeat: no-repeat;
  background-position-x: 28px;
  min-height: 240px;
  border-radius: 16px;

  @media screen and (max-width: 1023px) {
    display: none;
  }
`

const CardStyleOverrides = {
  borderRadius: "24px",
}

export const OfferAcceptedView: React.FC<OfferAcceptedViewProps> = ({
  fetchedStatus,
  friendlyId,
}) => {
  const history = useHistory()

  // Set UTMs for Offer Accepted (aka. pnf-returns) experiment if none are present
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)

    // Taken from: https://www.figma.com/board/JPCNDj3YrxK4bERQ6Jsa1N/PNF-Experience---Implementation-Support-Info?node-id=39-2592&t=Vbu5YvGoUdaI4q9y-0
    if (!searchParams.has("utm_source")) searchParams.set("utm_source", "Online")
    if (!searchParams.has("utm_medium")) searchParams.set("utm_medium", "MyMojo")
    history.replace({ search: searchParams.toString() })
  }, [history])

  // Create mortgageExpert object from API data with fallbacks
  const mortgageExpert = {
    name: fetchedStatus?.opportunityOwnerTextForEmail || "Mojo Mortgages",
    title: fetchedStatus?.flexRoles2 || "Mortgage Expert",
    email: fetchedStatus?.ownerEmailAddress || "",
    avatar: MojoMiniLogo,
  }

  // State for managing MIP URL error display
  const [showMipError, setShowMipError] = useState(false)

  return (
    <StatusContainer>
      {/* Header Section */}
      <HeaderSection>
        <Typography.H1 margin="0 0 16px 0">Your house hunting toolkit</Typography.H1>
        <Typography.LargeBody color="secondary" margin="0">
          Everything you need to find your new home with confidence.
        </Typography.LargeBody>
      </HeaderSection>

      {/* Offer Accepted Banner & Contact Section */}
      <Card
        theme="default"
        style={CardStyleOverrides}
        content={
          <HadAnOfferAcceptedContainer>
            <HadAnOfferAcceptedContent>
              <OfferAcceptedBanner>
                <p>Had an offer accepted on a property?</p>
              </OfferAcceptedBanner>

              <TextContent>
                <OfferAcceptedHeading>Let's get your mortgage sorted 🎉</OfferAcceptedHeading>
                <OfferAcceptedBody>
                  To move quickly and secure your mortgage deal, please contact your Mortgage
                  Expert. They're ready to discuss your accepted offer and guide you through the
                  next steps.
                </OfferAcceptedBody>
                <SmallFontButton>
                  <Button
                    type={ButtonType.Solid}
                    text="Get a mortgage offer"
                    onClick={() => {
                      LogGaEvent({
                        action: EventAction.buttonClicked,
                        event_category: EventCategory.functionalInteraction,
                        event_label: "CTA Clicked - Offer Accepted - Status Page",
                      })

                      // Add appointment ID if it doesn't exist
                      const searchParams = new URLSearchParams(location.search)
                      if (!searchParams.has("appointmentTypeId"))
                        searchParams.set("appointmentTypeId", "79489776") // PNF Return (Property Found)

                      // Navigate the user to the 'get-a-mortgage-offer' booking page
                      window.location.href = `/results/${friendlyId}/get-a-mortgage-offer/book?${searchParams.toString()}`
                    }}
                  />
                </SmallFontButton>
              </TextContent>
            </HadAnOfferAcceptedContent>
            <HadAnOfferAcceptedIllustration />
          </HadAnOfferAcceptedContainer>
        }
      />

      {/* Two Column Cards */}
      <TwoColumnContainer>
        {/* Property Research Card */}
        <Card
          theme="default"
          style={CardStyleOverrides}
          content={
            <PropertyCardContent>
              <div>
                <PropertyImageContainer>
                  <GreenBadge>
                    <div className="dot">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path
                          d="M7 1L3 5L1 3"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span>No known restrictions</span>
                  </GreenBadge>

                  <PropertyBadge position={{ bottom: 98, right: 0 }}>
                    <span>Mobile coverage</span>
                    <SignalBars>
                      <SignalBar height={12} active={true} />
                      <SignalBar height={15} active={true} />
                      <SignalBar height={18} active={true} />
                      <SignalBar height={20} active={false} />
                    </SignalBars>
                  </PropertyBadge>

                  <PropertyBadge position={{ bottom: 47, left: 0 }}>
                    <span>EPC</span>
                    <EPCRating>71 C</EPCRating>
                  </PropertyBadge>
                </PropertyImageContainer>

                <TextContent>
                  <Typography.H4 margin="0 0 8px 0">View & research properties</Typography.H4>
                  <Typography.Body color="secondary" margin="0 0 16px 0">
                    Run a free Property Report to understand a home's details and avoid any
                    surprises
                  </Typography.Body>
                </TextContent>
              </div>

              <SmallFontButton>
                <Button
                  type={ButtonType.Outline}
                  text="Create a property report"
                  onClick={() => {
                    window.open(
                      `${PROPERTY_REPORT_URL}/application/${friendlyId}/property-report/`,
                      "_blank"
                    )
                  }}
                />
              </SmallFontButton>
            </PropertyCardContent>
          }
        />

        {/* MIP Card */}
        <Card
          theme="default"
          style={CardStyleOverrides}
          content={
            <MIPCardContent>
              <div>
                <MIPImageContainer />

                <TextContent>
                  <Typography.H4 margin="0 0 8px 0">Mortgage in Principle</Typography.H4>
                  <Typography.Body color="secondary" margin="0">
                    Your affordability has been verified by a Mortgage Expert, so you're good to
                    start viewing properties.
                  </Typography.Body>
                </TextContent>
              </div>

              <SmallFontButton>
                <Button
                  type={ButtonType.Outline}
                  text="View your Mortgage in Principle"
                  onClick={() => {
                    if (fetchedStatus?.mipUrl) {
                      setShowMipError(false) // Clear any existing error
                      window.open(fetchedStatus.mipUrl, "_blank")
                    } else {
                      setShowMipError(true) // Show error when no URL available
                    }
                  }}
                />
                {showMipError && (
                  <ErrorMessage>
                    Sorry, we could not locate your document. Please try again later or contact your
                    Mortgage Expert.
                  </ErrorMessage>
                )}
              </SmallFontButton>
            </MIPCardContent>
          }
        />
      </TwoColumnContainer>

      {/* Expert Questions Section */}
      <Card
        theme="default"
        style={CardStyleOverrides}
        content={
          <SectionContent>
            <TextContent>
              <Typography.H4>Got a question for your expert?</Typography.H4>
              <Typography.Body>
                For any other questions or updates about your mortgage journey, your dedicated
                Mortgage Expert, is here to help guide you through the mortgage process.
              </Typography.Body>
            </TextContent>

            <MortgageExpertCardComponent mortgageExpert={mortgageExpert} />
          </SectionContent>
        }
      />

      {/* Conveyancing Section */}
      <ConveyancingCardComponent friendlyId={friendlyId} />
    </StatusContainer>
  )
}
